import type { ConnectShareTokenCreateResult } from '@/lib/play/useConnectShareTokenSession'
import { readPlayApiJsonSafely } from '@/lib/play/playApiResponse'

const PRODUCTION_SHARE_USER_MESSAGE =
  "We couldn't prepare your dog's Dog Card. Please try again."

type ProductionShareSuccess = {
  success: true
  token?: {
    connectionToken?: string
    shareUrl?: string
    expiresAt?: string
  }
}

type PreviewShareSuccess = {
  success: true
  dogCardPath?: string
  shareUrl?: string
  expiresAt?: string
}

function absoluteDogCardUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http')) return pathOrUrl
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`
  }
  return pathOrUrl
}

export function resolveProductionConnectShareResult(
  data: ProductionShareSuccess
): ConnectShareTokenCreateResult {
  const rawToken = data.token?.connectionToken?.trim()
  const shareUrl = data.token?.shareUrl?.trim()
  const dogCardPath = rawToken ? `/play/connect/${encodeURIComponent(rawToken)}` : null
  const resolvedUrl = shareUrl || (dogCardPath ? absoluteDogCardUrl(dogCardPath) : null)

  if (!resolvedUrl) {
    return { ok: false, message: PRODUCTION_SHARE_USER_MESSAGE }
  }

  return {
    ok: true,
    dogCardUrl: resolvedUrl,
    expiresAt: data.token?.expiresAt ?? null,
  }
}

export function resolvePreviewConnectShareResult(
  data: PreviewShareSuccess,
  fallbackMessage: string
): ConnectShareTokenCreateResult {
  const dogCardPath = data.dogCardPath?.trim()
  if (!dogCardPath) {
    return { ok: false, message: fallbackMessage }
  }

  const absoluteUrl =
    data.shareUrl?.trim() ||
    (dogCardPath.startsWith('http') ? dogCardPath : absoluteDogCardUrl(dogCardPath))

  return {
    ok: true,
    dogCardUrl: absoluteUrl,
    expiresAt: data.expiresAt ?? null,
  }
}

export async function parseProductionShareResponse(
  response: Response
): Promise<ConnectShareTokenCreateResult> {
  const parsed = await readPlayApiJsonSafely<ProductionShareSuccess>(
    response,
    PRODUCTION_SHARE_USER_MESSAGE
  )
  if (!parsed.ok) {
    return { ok: false, message: parsed.message }
  }
  if (!parsed.data.success) {
    return { ok: false, message: PRODUCTION_SHARE_USER_MESSAGE }
  }
  return resolveProductionConnectShareResult(parsed.data)
}

export async function parsePreviewShareResponse(
  response: Response,
  fallbackMessage: string
): Promise<ConnectShareTokenCreateResult> {
  const parsed = await readPlayApiJsonSafely<PreviewShareSuccess>(response, fallbackMessage)
  if (!parsed.ok) {
    return { ok: false, message: parsed.message }
  }
  if (!parsed.data.success) {
    return { ok: false, message: fallbackMessage }
  }
  return resolvePreviewConnectShareResult(parsed.data, fallbackMessage)
}
